import { AlertsError, Popper } from 'components';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Wrapper = styled.div<{ hasError: boolean }>`
    display: flex;
    flex-direction: column;
    color: ${({ theme, hasError }) =>
        hasError ? theme.palette.error.main : theme.palette.grey[400]};
    gap: 3px;
    margin-right: 1rem;

    label {
        padding-left: 0.25rem;
        font-size: 0.75rem;
    }

    input {
        padding: 3px 5px;
        border: none;
        white-space: nowrap;
    }

    div {
        padding: 3px 5px;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        border: 1px solid
            ${({ theme, hasError }) =>
                hasError ? theme.palette.error.main : theme.palette.grey[400]};
        border-radius: 3px;
    }
`;

interface TimeInputProps {
    required: boolean;
    label: string;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    validate: () => boolean;
}

export const TimeInput: FC<TimeInputProps> = ({
    label,
    value,
    onChange,
    required,
    validate
}: TimeInputProps) => {
    const { t } = useTranslation();
    const [hasError, setHasError] = useState(false);
    const [showPopper, setShowPopper] = useState(false);
    const iconRef = useRef<HTMLDivElement>();

    useEffect(() => {
        setHasError(validate());
    }, [value, setHasError, validate]);

    return (
        <Wrapper hasError={hasError}>
            <label>{label}</label>
            <div>
                <input
                    type="time"
                    required={required}
                    value={value}
                    onChange={(event) => {
                        onChange(event);
                    }}
                />
                {hasError && (
                    <span
                        ref={iconRef}
                        onMouseEnter={() => setShowPopper(true)}
                        onMouseLeave={() => setShowPopper(false)}>
                        <Popper
                            isOpen={showPopper}
                            placement="left"
                            anchorElement={iconRef.current}
                            hasArrow>
                            {t('error.from_to_same')}
                        </Popper>
                        <AlertsError size={16} color="error" />
                    </span>
                )}
            </div>
        </Wrapper>
    );
};
